// #region Imports
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import { styled } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import { ConfirmResponse, useConfirmDialog } from "_shared/_providers/dialogs/confirm/confirm-dialog";
import downloadFile from "_shared/_utilities/download-file";
import Guid from "_shared/guids/guid";
import { useLazyDownloadDocumentQuery } from "documents/_api/document-api";
import { showErrorSnackbar } from "_shared/_errors/error-snackbar";
import DocumentSelector from "documents/_views/selectors/document-selector";
import DocumentUploadOverlay from "documents/_views/uploader/document-upload-overlay";
import Document from "documents/document";
import React, { useCallback, useMemo, useState } from "react";
import { useSession } from "users/__session/session-context";
import { Masked } from "work/masked-fields";
import ProposalBuilder from "work/proposals/__builder/proposal-builder";
import ProposalIssueList from "work/proposals/issues/_views/proposal-issue-list";
import ProposalIssues from "work/proposals/issues/proposal-issues";
import ProposalField from "work/proposals/proposal-fields/proposal-field";
import SelectedDocumentList, {
  type DocumentDraftRemovals,
} from "work/work-agreements/contracts/_views/selected-document-list";
import SavedDraftCollections from "work/proposals/_views/drafts/saved-draft-collections";
import { WorkDocumentType } from "work/work-agreements/contracts/work-document-type";
import WorkDocument from "work/work-agreements/contracts/work-document";
import { xor } from "lodash";
import ResizableTemplatePanel from "work/proposals/_views/_shared/resizable-template-panel";
import { MaskedProposalField, maskedProposalFieldNotice } from "work/proposals/_views/_shared/masked-field-notice";
import {
  paneIsCompact,
  workPaneProseInset,
} from "work/_views/work-pane-layout";
import { useWorkPaneIsCompact } from "work/_views/use-work-pane-size";
// #endregion

// #region Styled Components
const DOCUMENT_ROW_MAX_WIDTH = "550px";

const ListContainer = styled(Grid)(({ theme }) => ({
  flex: 1,
  flexWrap: "nowrap",
  height: "100%",
  minHeight: "22rem",
  width: "100%",
  [paneIsCompact(theme)]: {
    flexDirection: "column",
    flexWrap: "nowrap",
    height: "auto",
    minHeight: 0,
  },
}));
const SelectorDivider = styled(Grid)(({ theme }) => ({
  [paneIsCompact(theme)]: {
    display: "none",
  },
}));
const SelectedDocsList = styled(SelectedDocumentList)(({ theme }) => ({
  // No top margin under a narrower pane: it stood the collection's title 16px further off the switch
  // or notice above it than anything else on the tab (#6041).
  maxHeight: "60vh",
  // No left margin: the table meets the resize divider, the way the fee schedule's does. The prose
  // above it carries its own inset instead — see `workPaneProseInset`.
  minWidth: 0,
  overflowY: "auto",
  position: "relative",
  flex: 1,
  [paneIsCompact(theme)]: {
    boxSizing: "border-box",
    marginLeft: 0,
    maxHeight: "none",
    maxWidth: "none",
    minWidth: 0,
    width: "100%",
    "&&": {
      marginLeft: 0,
      maxWidth: "none",
      minWidth: 0,
      width: "100%",
    },
  },
}));
const HeadControls = styled("div")(({ theme }) => ({
  ...workPaneProseInset(theme),
  alignItems: "flex-start",
  boxSizing: "border-box",
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(1),
  maxWidth: DOCUMENT_ROW_MAX_WIDTH,
  width: "100%",
  [paneIsCompact(theme)]: {
    maxWidth: "none",
  },
}));
const SelectedDocsPanel = styled("div")(() => ({
  display: "flex",
  flex: 1,
  flexDirection: "column",
  minWidth: 0,
  width: "100%",
}));
const Uploader = styled(DocumentUploadOverlay)(() => ({
  backgroundColor: "rgba(250, 250, 250, 0.5)",
  backdropFilter: "blur(5px) saturate(200%)",
  position: "absolute",
  top: 0,
  left: 0,
  margin: "1rem",
  width: "calc(100% - 2rem)",
  height: "calc(100% - 2rem)",
  zIndex: 9999,
}));
// #endregion

type PolicyUploadProps = {
  activeReviewField?: ProposalField;
  proposalBuilder: ProposalBuilder;
  proposalId?: Guid;
  issues?: ProposalIssues;
  negotiationId?: string;
  viewerScope?: string;
  disableEditing?: boolean;
  fieldLockTooltip?: string;
  /** A side pane is taking the row's width, so the templates column gives up its own — see `ResizableTemplatePanel`. */
  isDrawerOpen: boolean;
  showPendingChangeHighlights?: boolean;
  /** The draft's collections as last saved, which a removed document's Undo returns to (#6995). */
  savedCollections?: SavedDraftCollections;
  onProposalBuilderUpdated: (updatedProposalBuilder: ProposalBuilder) => void;
  /** Moves the page to the field an issue is about, as "Prev / Next Issue" would. */
  onTraverseToNewField?: (field: ProposalField) => void;
};

export default function PoliciesTab(props: Readonly<PolicyUploadProps>) {
  const {
    proposalBuilder,
    proposalId,
    negotiationId,
    viewerScope,
    disableEditing,
    isDrawerOpen,
    fieldLockTooltip,
    activeReviewField,
    showPendingChangeHighlights,
    savedCollections,
    onProposalBuilderUpdated,
    onTraverseToNewField,
  } = props;

  // #region State
  // The template selector collapses to its summary when there is no room to hold it open. The pane
  // is what decides that here, not the window — an open comments panel takes a column out of it.
  const selectorIsCompact = useWorkPaneIsCompact();
  const [creatingDocumentFromTemplate, setCreatingDocumentFromTemplate] = useState<Guid | null>(null);
  const [isDownloadingFile, setIsDownloadingFile] = useState<Guid | null>(null);
  
  const pendingChangeFields = showPendingChangeHighlights
    ? proposalBuilder.sessionHistory?.flatMap((change) => change.field ? [change.field] : []) ?? []
    : [];

  // #region Hooks
  const confirm = useConfirmDialog();
  const session = useSession();
  const [downloadDocument] = useLazyDownloadDocumentQuery();

  // #region Memoized Values
  const issues = useMemo(
    () =>
      props.issues?.entries.filter((issue) => {
        const candidates = [
          ProposalField.Policies,
          ProposalField.VendorPolicies,
          ProposalField.ClientPolicies,
          ProposalField.VendorPolicy(issue.field.id ?? Guid.EMPTY),
          ProposalField.ClientPolicy(issue.field.id ?? Guid.EMPTY),
        ];

        return candidates.some((field) => field.isEqualTo(issue.field));
      }),
    [props.issues?.entries]
  );

  const selectedDocuments = useMemo(() => {
    if (session.context?.viewingAsVendor) {
      if (Masked.isMasked(proposalBuilder.currentSpec.workAgreement.vendorPolicies)) return [];
      return proposalBuilder.currentSpec.workAgreement.vendorPolicies ?? [];
    } else {
      if (Masked.isMasked(proposalBuilder.currentSpec.workAgreement.clientPolicies)) return [];
      return proposalBuilder.currentSpec.workAgreement.clientPolicies ?? [];
    }
  }, [
    proposalBuilder.currentSpec.workAgreement.vendorPolicies,
    proposalBuilder.currentSpec.workAgreement.clientPolicies,
    session.context?.viewingAsVendor
  ]);

  const selectedDocumentIds = useMemo(
    () => selectedDocuments.map((doc) => doc.id),
    [selectedDocuments]
  );

  // The policies this draft removed since its last save, each with an Undo (#6995). Memoized for
  // stability: the list draws them as rows.
  const policyType = session.context?.viewingAsVendor ? WorkDocumentType.VendorPolicy : WorkDocumentType.ClientPolicy;
  const draftRemovals = useMemo<DocumentDraftRemovals | undefined>(
    () =>
      savedCollections
        ? {
            documents: savedCollections.removedDocuments(policyType, proposalBuilder.currentSpec),
            onDocumentUndone: (document) =>
              onProposalBuilderUpdated(savedCollections.restoreDocument(policyType, document.id, proposalBuilder)),
          }
        : undefined,
    [onProposalBuilderUpdated, policyType, proposalBuilder, savedCollections],
  );

  const selectedTemplateIds = useMemo(
    () => selectedDocuments.flatMap((doc) => doc.templateIds),
    [selectedDocuments]
  );
  // #endregion

  // #region Functions
  const handlePolicyDocumentRemoved = useCallback(async (documentId: Guid): Promise<void> => {
    if (
      Masked.isMasked(proposalBuilder.currentSpec.workAgreement.vendorPolicies) ||
      Masked.isMasked(proposalBuilder.currentSpec.workAgreement.clientPolicies)
    ) {
      throw new Error("You are not permitted to view contracts.");
    }
    const response = await confirm({
      title: "Remove Policy Document",
      message: "Are you sure you want to remove this policy document?",
    });

    if (response === ConfirmResponse.Cancel) return;

    let updatedProposalBuilder: ProposalBuilder;
    if (session.context?.viewingAsVendor) {
      updatedProposalBuilder = proposalBuilder.setVendorPolicies(
        [...(proposalBuilder.currentSpec.workAgreement.vendorPolicies ?? [])].filter(
          (doc) => !doc.id.isEqualTo(documentId)
        )
      );
    } else {
      updatedProposalBuilder = proposalBuilder.setClientPolicies(
        [...(proposalBuilder.currentSpec.workAgreement.clientPolicies ?? [])].filter(
          (doc) => !doc.id.isEqualTo(documentId)
        )
      );
    }

    onProposalBuilderUpdated(updatedProposalBuilder);
  }, [confirm, onProposalBuilderUpdated, proposalBuilder, session.context?.viewingAsVendor]);

  const handleDownloadDocumentById = useCallback(
    async (documentId: Guid, event?: React.MouseEvent<HTMLButtonElement, MouseEvent>): Promise<void> => {
      event?.stopPropagation();
      try {
        setIsDownloadingFile(documentId);
        const policyDocument = await downloadDocument({ documentId: documentId.value }).unwrap();
        await downloadFile(policyDocument);
      } catch (err) {
        console.error(err);
        showErrorSnackbar(err, { doing: "downloading the policy document", operation: "proposal.policies.download" });
      } finally {
        setIsDownloadingFile(null);
      }
    }, [downloadDocument]);

  const handleDocumentSelectionChanged = useCallback((selectedDocs: Document[], deselectedDocs: Document[]) => {
    if (
      Masked.isMasked(proposalBuilder.currentSpec.workAgreement.vendorPolicies) ||
      Masked.isMasked(proposalBuilder.currentSpec.workAgreement.clientPolicies)
    ) {
      throw new Error("You are not permitted to view contracts.");
    }

    let existingDocuments = session.context?.viewingAsVendor
      ? [...(proposalBuilder.currentSpec.workAgreement.vendorPolicies ?? [])]
      : [...(proposalBuilder.currentSpec.workAgreement.clientPolicies ?? [])];

    let currentDocuments = [...existingDocuments];

    for (const selectedDoc of selectedDocs) {
      if (!currentDocuments.some((doc) => doc.id.isEqualTo(selectedDoc.id))) {
        currentDocuments.push(
          WorkDocument.fromDocument(
            selectedDoc,
            session.context?.viewingAsVendor ? WorkDocumentType.VendorPolicy : WorkDocumentType.ClientPolicy
          )
        );
      }
    }

    for (const deselectedDoc of deselectedDocs) {
      currentDocuments = currentDocuments.filter((doc) => !doc.id.isEqualTo(deselectedDoc.id));
    }

    // If the document lists are the same, do not trigger an update
    const existingDocIds = existingDocuments.map(d => d.id.value);
    const currentDocIds = currentDocuments.map(d => d.id.value);

    if (xor(existingDocIds, currentDocIds).length === 0) return;

    const updatedProposalBuilder = session.context?.viewingAsVendor
      ? proposalBuilder.setVendorPolicies(currentDocuments)
      : proposalBuilder.setClientPolicies(currentDocuments);

    onProposalBuilderUpdated(updatedProposalBuilder);
  }, [onProposalBuilderUpdated, proposalBuilder, session.context?.viewingAsVendor]);

  const handlePolicyDocumentsAdded = useCallback((policyDocuments: Document[]) => {
    if (
      Masked.isMasked(proposalBuilder.currentSpec.workAgreement.vendorPolicies) ||
      Masked.isMasked(proposalBuilder.currentSpec.workAgreement.clientPolicies)
    ) {
      throw new Error("You are not permitted to view contracts.");
    }

    if (policyDocuments.length === 0) return;

    let currentDocuments = session.context?.viewingAsVendor
      ? proposalBuilder.currentSpec.workAgreement.vendorPolicies ?? []
      : proposalBuilder.currentSpec.workAgreement.clientPolicies ?? [];
    currentDocuments = [...currentDocuments];

    for (const policyDocument of policyDocuments) {
      if (currentDocuments.some((document) => document.id.isEqualTo(policyDocument.id))) continue;

      currentDocuments.push(
        WorkDocument.fromDocument(
          policyDocument,
          session.context?.viewingAsVendor ? WorkDocumentType.VendorPolicy : WorkDocumentType.ClientPolicy
        )
      );
    }

    let updatedProposalBuilder = proposalBuilder;
    if (session.context?.viewingAsVendor) {
      updatedProposalBuilder = updatedProposalBuilder.setVendorPolicies(currentDocuments);
    } else {
      updatedProposalBuilder = updatedProposalBuilder.setClientPolicies(currentDocuments);
    }

    setCreatingDocumentFromTemplate(null);
    onProposalBuilderUpdated(updatedProposalBuilder);
  }, [onProposalBuilderUpdated, proposalBuilder, session.context?.viewingAsVendor]);

  const handleBeginCreateDocumentFromTemplate = useCallback(async (templateId: Guid | undefined | null) => {
    if (!templateId) return;
    setCreatingDocumentFromTemplate(templateId);
    await handleDownloadDocumentById(templateId);
  }, [handleDownloadDocumentById]);

  // A revert restores a document's prior value through the SAME `setVendorPolicies`/`setClientPolicies`
  // builder path any other document edit uses, so it flows to `queueProposalUpdate` as a
  // `CollectionItemUpdated`/`CollectionItemAdded` op — never a raw `FieldSet`.
  const handlePolicyDocumentReverted = useCallback((document: WorkDocument) => {
    if (
      Masked.isMasked(proposalBuilder.currentSpec.workAgreement.vendorPolicies) ||
      Masked.isMasked(proposalBuilder.currentSpec.workAgreement.clientPolicies)
    ) {
      return;
    }

    const existingDocuments = session.context?.viewingAsVendor
      ? proposalBuilder.currentSpec.workAgreement.vendorPolicies ?? []
      : proposalBuilder.currentSpec.workAgreement.clientPolicies ?? [];
    const updatedDocuments = existingDocuments.some((existing) => existing.id.isEqualTo(document.id))
      ? existingDocuments.map((existing) => (existing.id.isEqualTo(document.id) ? document : existing))
      : [...existingDocuments, document];

    const updatedProposalBuilder = session.context?.viewingAsVendor
      ? proposalBuilder.setVendorPolicies(updatedDocuments)
      : proposalBuilder.setClientPolicies(updatedDocuments);

    onProposalBuilderUpdated(updatedProposalBuilder);
  }, [onProposalBuilderUpdated, proposalBuilder, session.context?.viewingAsVendor]);
  // #endregion

  // #region Render
  if ((session.context?.viewingAsVendor && Masked.isMasked(proposalBuilder.currentSpec.workAgreement.vendorPolicies)) ||
    (!session.context?.viewingAsVendor && Masked.isMasked(proposalBuilder.currentSpec.workAgreement.clientPolicies))) {
    return (
      <Typography variant="body1">{maskedProposalFieldNotice(MaskedProposalField.Policies)}</Typography>
    );
  }

  return (
    <>
      <ListContainer container direction="row">
        {!disableEditing && (
          <>
            <ResizableTemplatePanel minimized={isDrawerOpen} selectedCount={selectedDocumentIds.length}>
              <DocumentSelector
                compact={selectorIsCompact}
                documentType="policy"
                proposalId={proposalId}
                selectedDocumentIds={selectedDocumentIds}
                selectedTemplateIds={selectedTemplateIds}
                onDocumentSelectionChanged={handleDocumentSelectionChanged}
                onCreateDocumentFromTemplate={handleBeginCreateDocumentFromTemplate}
              />
            </ResizableTemplatePanel>
            <SelectorDivider>
              <Divider orientation="vertical" />
            </SelectorDivider>
          </>
        )}
        <SelectedDocsPanel>
          {(issues?.length ?? 0) > 0 && (
            <HeadControls>
              <ProposalIssueList
                issues={issues ?? []}
                activeReviewField={activeReviewField}
                onIssueSelected={(issue) => onTraverseToNewField?.(issue.field)}
              />
            </HeadControls>
          )}
          <SelectedDocsList
            documents={selectedDocuments}
            proposalId={proposalId}
            pendingChangeFields={pendingChangeFields}
            documentType={
              session.context?.viewingAsVendor ? WorkDocumentType.VendorPolicy : WorkDocumentType.ClientPolicy
            }
            downloadingFileId={isDownloadingFile}
            negotiationId={negotiationId}
            viewerScope={viewerScope}
            disableEditing={disableEditing}
            fieldLockTooltip={fieldLockTooltip}
            onDownload={handleDownloadDocumentById}
            onDocumentRemoved={handlePolicyDocumentRemoved}
            onDocumentAdded={(document) => handlePolicyDocumentsAdded([document])}
            onDocumentsAdded={handlePolicyDocumentsAdded}
            onDocumentReverted={handlePolicyDocumentReverted}
            draftRemovals={draftRemovals}
          />
        </SelectedDocsPanel>
      </ListContainer>
      {creatingDocumentFromTemplate && (
        <Uploader
          documentType={
            session.context?.viewingAsVendor ? WorkDocumentType.VendorPolicy : WorkDocumentType.ClientPolicy
          }
          proposalId={proposalId}
          templateId={creatingDocumentFromTemplate}
          onDocumentUploaded={(document) => handlePolicyDocumentsAdded([document])}
          onClose={() => setCreatingDocumentFromTemplate(null)}
        />
      )}
    </>
  );
}
